import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function CredentialsStep({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.email_sent) toast.success(`Codice inviato a ${data.email}`);
      else toast.warning("Impossibile inviare l'email con il codice. Contatta Marigot Informatica.");
      onDone(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 h-11" placeholder="nome@azienda.it" data-testid="login-email-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11" data-testid="login-password-input" />
        </div>
      </div>
      {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-500 active:scale-[0.98] transition-transform" data-testid="login-submit-button">
        {loading ? <Loader2 className="animate-spin" size={18} /> : "Continua"}
      </Button>
    </form>
  );
}

function OtpStep({ otp, onBack }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(60);
  useEffect(() => {
    const t = setInterval(() => setTimer((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const verify = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", { otp_token: otp.otp_token, code });
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success(`Benvenuto, ${data.name || data.email}`);
      navigate(data.role === "admin" ? "/admin" : "/portale");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-otp", { otp_token: otp.otp_token });
      data.email_sent ? toast.success("Nuovo codice inviato") : toast.error("Invio email non riuscito");
      setTimer(60);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={verify} className="space-y-6" data-testid="otp-form">
      <p className="text-sm text-slate-400">
        Abbiamo inviato un codice a 6 cifre a <span className="text-slate-100 font-medium">{otp.email}</span>. Il codice scade tra 10 minuti.
      </p>
      <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input-code">
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg font-mono border-slate-700" />
          ))}
        </InputOTPGroup>
      </InputOTP>
      {error && <p className="text-sm text-rose-400" data-testid="otp-error">{error}</p>}
      <Button type="submit" disabled={loading || code.length < 6} className="w-full h-11 bg-blue-600 hover:bg-blue-500" data-testid="otp-verify-button">
        {loading ? <Loader2 className="animate-spin" size={18} /> : "Verifica e accedi"}
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="text-slate-400 hover:text-slate-100 flex items-center gap-1" data-testid="otp-back-button">
          <ArrowLeft size={14} />Indietro
        </button>
        <button type="button" disabled={timer > 0} onClick={resend} className="text-blue-400 disabled:text-slate-600" data-testid="otp-resend-button">
          {timer > 0 ? `Invia nuovo codice (${timer}s)` : "Invia nuovo codice"}
        </button>
      </div>
    </form>
  );
}

export default function Login() {
  const [otp, setOtp] = useState(null);
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0B0F17] text-slate-100">
      <div className="relative hidden lg:block overflow-hidden noise">
        <img src={HERO} alt="Data center" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="self-start rounded-md bg-white px-4 py-2.5"><img src="/brand/logo-marigot.png" alt="Marigot Informatica" className="h-11 w-auto" data-testid="login-logo" /></div>
          <div className="max-w-lg">
            <div className="text-xs uppercase tracking-[0.25em] text-blue-400 mb-4">Area riservata clienti</div>
            <h1 className="text-5xl font-semibold tracking-tight leading-[1.05]">Tutti i tuoi servizi IT, in un'unica bacheca.</h1>
            <p className="mt-5 text-slate-400">Abbonamenti Firewall, Microsoft 365, centralino 3CX, fatture e documenti: sempre disponibili, protetti da doppia autenticazione.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 grid-bg">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8">
            <div className="lg:hidden inline-block rounded-md bg-white px-3 py-2 mb-8"><img src="/brand/logo-marigot.png" alt="Marigot Informatica" className="h-10 w-auto" data-testid="login-logo-mobile" /></div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1 text-xs text-slate-400 mb-6">
              <ShieldCheck size={13} className="text-emerald-400" />{otp ? "Passo 2 di 2 · Verifica" : "Passo 1 di 2 · Credenziali"}
            </div>
            <h2 className="text-3xl font-semibold tracking-tight" data-testid="login-title">{otp ? "Verifica identità" : "Bacheca Marigot Informatica"}</h2>
            <p className="mt-2 text-sm text-slate-400">{otp ? "Inserisci il codice ricevuto via email." : "Accedi con le credenziali fornite da Marigot Informatica."}</p>
          </div>
          {otp ? <OtpStep otp={otp} onBack={() => setOtp(null)} /> : <CredentialsStep onDone={setOtp} />}
        </div>
      </div>
    </div>
  );
}
